<template>
  <!-- breadcrumb -->
  <div class="flex items-center my-4">
    <a :href="collectionsPath" class="text-12px">{{ $t('post_index_collection.collections') }}</a>
    <img src="../../../../images/breadcrumb_arrow.svg" class="inline-block mx-2 w-4 h-4" alt="Breadcrumb Arrow" />
    <span class="text-12px text-grey-5">{{ $t(`public_profile_layout_component.collection_sort`) }}</span>
  </div>

  <div class="w-full h-full flex flex-col">
    <!-- content -->
    <div class="flex flex-col">
      <!-- loading -->
      <template v-if="state.initialLoading">
        <div class="flex flex-col items-center justify-center flex-1 mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
          <p class="text-16px font-medium text-center">
            <img src="../../../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
          </p>
        </div>
      </template>

      <!-- no items -->
      <template v-else-if="sortedItems.length === 0">
        <div class="flex flex-col items-center justify-center flex-1 mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
          <img src="../../../../images/sad.svg" class="block w-12 h-12 mb-4" />
          <p class="text-16px font-medium text-center">
            {{ $t('models.index.no_model_now') }}
            <br>
            {{ $t('models.index.create_your_model') }}
          </p>
        </div>
      </template>

      <!-- items -->
      <template v-else>
        <div class="model-container flex flex-col gap-y-2 flex-1 overflow-auto scrollbar pr-1 -mr-3">
          <collection-sort-item
            v-for="(collection, idx) in sortedItems"
            :key="`collection-${collection.id}`"
            :data-model-id="collection.id"
            source="ProfileSortModels"
            class="sorted-model-item"
            :item="collection"
            :is-pinned="false"
            :show-pin-icon="false"
            :order-type="'custom_order'"
            :is-sortable="true"
            :index="collection.sortOrder"
            @moveToTop="() => moveItem(collection.id, 'top')"
            @moveToBottom="() => moveItem(collection.id, 'bottom')"
          />
        </div>

        <!-- save button -->
        <div class="w-full mt-4 bg-gray-50 flex justify-center items-center">
          <a :href="collectionsPath" class="btn text-12px hover:text-gray-150">
            {{ $t('models.index.cancel') }}
          </a>
          <button
            class="btn btn-primary flex h-8 py-1"
            @click="submitSortOrder"
            :disabled="state.isSaving">
            <img src="../../../../images/icons/save_24.svg" class="w-6 h-6" />
            <span class="ml-2 inline-block text-14px">
              {{ state.isSaving ? 'Saving...' : $t('models.index.save_sort') }}
            </span>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, onUnmounted } from "vue";
import Sortable from 'sortablejs';

export default defineComponent({
  props: {
    scope: {
      type: String,
      default: 'collections'
    },
    view: {
      type: String,
      default: 'sorting'
    }
  },

  setup(props) {
    const state = reactive({
      currentUser: null,
      isSaving: false,
      allItems: [],
      sortedItems: [],
      initialLoading: true,
      isOwner: false
    });

    const sortableInstance = ref(null);

    const sortedItems = computed(() =>
      state.sortedItems.sort((a, b) => a.sortOrder - b.sortOrder)
    );

    const collectionsPath = computed(() => {
      const currentPath = window.location.pathname;
      return currentPath.split('/').slice(0, -1).join('/');
    });

    const moveItem = (id, direction) => {
      const currentIndex = state.sortedItems.findIndex(item => item.id === id);
      if (currentIndex === -1) return;

      let newIndex;
      switch(direction) {
        case 'top':
          newIndex = 0;
          break;
        case 'bottom':
          newIndex = state.sortedItems.length - 1;
          break;
        default:
          return;
      }

      if (currentIndex === newIndex) return;

      reorderItems(currentIndex, newIndex);
    };

    const reorderItems = (fromIndex, toIndex) => {
      const [movedItem] = state.sortedItems.splice(fromIndex, 1);
      state.sortedItems.splice(toIndex, 0, movedItem);

      state.sortedItems.forEach((item, index) => {
        item.sortOrder = index;
      });
    };

    const fetchCurrentUser = async () => {
      try {
        const response = await fetch('/api/v1/profile/current_user', {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          state.currentUser = await response.json();
        } else {
          throw new Error('Failed to fetch current user');
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    // 確保 ID 類型為數字
    const fetchSortedCollections = async () => {
      try {
        const response = await fetch('/api/v1/profile/orders_config?mode=profile_collections');
        if (response.ok) {
          const data = await response.json();

          // 將字符串 ID 轉換為數字
          const sortOrderIds = (data.normal || []).map(id => Number(id));

          return sortOrderIds;
        }
      } catch (error) {
        console.error('Error fetching sorted data:', error);
        return [];
      }
    };

    const fetchAllCollections = async () => {
      const url = `/api/v1/profiles/${state.currentUser.username}/get_designer_collections`;
      const params = new URLSearchParams({
        mode: 'view_all',
      });

      try {
        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          const data = await response.json();
          return {
            items: data.data || [],
            isOwner: data.is_owner
          };
        }
      } catch (error) {
        console.error('Error fetching collection data:', error);
        return { items: [], isOwner: false };
      }
    };

    // 確保 ID 比較使用數字類型
    const buildSortedItems = (items, sortOrderIds) => {
      return items.map(item => {
        // 確保 item.id 也是數字類型進行比較
        const itemId = Number(item.id);
        const orderIndex = sortOrderIds.indexOf(itemId);

        const result = {
          ...item,
          id: itemId,  // 確保 ID 是數字
          sortOrder: orderIndex >= 0 ? orderIndex : items.length + itemId
        };

        return result;
      }).sort((a, b) => a.sortOrder - b.sortOrder);
    };

    // 重新計算連續的索引順序
    const recalculateIndexOrder = () => {
      state.sortedItems.forEach((item, index) => {
        item.sortOrder = index;
      });
    };

    const submitSortOrder = async () => {
      if (state.isSaving) return;
      state.isSaving = true;

      try {
        // 確保提交的也是數字陣列
        const normalOrder = sortedItems.value.map(item => Number(item.id));

        const response = await fetch('/api/v1/profile/orders_config', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: JSON.stringify({
            mode: 'profile_collections',
            normal_order: normalOrder,
            pinned_order: []
          })
        });

        if (response.ok) {
          window.location.href = collectionsPath.value;
        } else {
          throw new Error('Failed to save sort order');
        }
      } catch (error) {
        console.error('Error saving sort order:', error);
        alert('保存排序失敗，請重試。');
      } finally {
        state.isSaving = false;
      }
    };

    const initSortable = () => {
      const container = document.querySelector('.model-container');
      if (!container) {
        console.error('Cannot find model container');
        return;
      }

      sortableInstance.value = Sortable.create(container, {
        animation: 150,
        group: 'collections',
        disabled: false,
        onEnd: syncOrderFromDOM
      });
    };

    const syncOrderFromDOM = () => {
      const newOrder = Array.from(document.querySelectorAll('.model-container > div'))
        .map(el => Number(el.dataset.modelId));

      newOrder.forEach((id, index) => {
        const item = state.sortedItems.find(item => item.id === id);
        if (item) {
          item.sortOrder = index;
        }
      });
    };

    onMounted(async () => {
      try {
        await fetchCurrentUser();
        if (!state.currentUser) {
          console.error('Failed to fetch current user');
          return;
        }

        const [{ items, isOwner }, sortOrderIds] = await Promise.all([
          fetchAllCollections(),
          fetchSortedCollections()
        ]);

        state.allItems = items;
        state.sortedItems = buildSortedItems(items, sortOrderIds);
        state.isOwner = isOwner;
        recalculateIndexOrder();

        setTimeout(() => {
          initSortable();
        }, 100);
      } catch (error) {
        console.error('Error in onMounted:', error);
      } finally {
        state.initialLoading = false;
      }
    });

    onUnmounted(() => {
      if (sortableInstance.value) {
        sortableInstance.value.destroy();
      }
    });

    return {
      state,
      sortedItems,
      collectionsPath,
      moveItem,
      submitSortOrder,
    };
  }
});
</script>